import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Chip, FormControlLabel, Switch,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import api from '../../api/client';

interface StatusType {
  id: number; code: string; name: string; color: string;
  is_available: boolean; sort_order: number; is_active: boolean;
}

export default function StatusTypesPage() {
  const [statuses, setStatuses] = useState<StatusType[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<StatusType | null>(null);
  const [form, setForm] = useState({ code: '', name: '', color: '#9e9e9e', is_available: false, sort_order: 0 });

  const load = () => api.get('/status-types/all').then((r) => setStatuses(r.data));
  useEffect(() => { load(); }, []);

  const handleOpen = (st?: StatusType) => {
    if (st) {
      setEditing(st);
      setForm({ code: st.code, name: st.name, color: st.color, is_available: st.is_available, sort_order: st.sort_order });
    } else {
      setEditing(null);
      setForm({ code: '', name: '', color: '#9e9e9e', is_available: false, sort_order: statuses.length + 1 });
    }
    setOpen(true);
  };

  const handleSave = async () => {
    if (editing) {
      await api.patch(`/status-types/${editing.id}`, form);
    } else {
      await api.post('/status-types', form);
    }
    setOpen(false);
    load();
  };

  const handleDelete = async (st: StatusType) => {
    if (!window.confirm(`Delete status type "${st.name}" (${st.code})? Units with this status will be unassigned.`)) return;
    await api.delete(`/status-types/${st.id}`);
    load();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Unit Status Types</Typography>
        <Button startIcon={<Add />} variant="contained" onClick={() => handleOpen()}>Add Status</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Color</TableCell>
              <TableCell>Available</TableCell>
              <TableCell>Active</TableCell>
              <TableCell width={60} />
            </TableRow>
          </TableHead>
          <TableBody>
            {statuses.map((st) => (
              <TableRow key={st.id}>
                <TableCell>{st.sort_order}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{st.code}</TableCell>
                <TableCell>{st.name}</TableCell>
                <TableCell><Chip size="small" label={st.color} sx={{ bgcolor: st.color, color: '#fff' }} /></TableCell>
                <TableCell>{st.is_available ? 'Yes' : 'No'}</TableCell>
                <TableCell><Chip size="small" label={st.is_active ? 'Active' : 'Inactive'} color={st.is_active ? 'success' : 'default'} /></TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpen(st)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(st)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Status Type' : 'New Status Type'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Code" margin="normal" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. AVL" />
          <TextField fullWidth label="Name" margin="normal" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Available" />
          <TextField fullWidth label="Color" type="color" margin="normal" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
          <TextField fullWidth label="Sort Order" type="number" margin="normal" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
          <FormControlLabel control={<Switch checked={form.is_available} onChange={(_, c) => setForm({ ...form, is_available: c })} />} label="Marks unit as available for dispatch" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
